import React from "react";
import { Link } from "react-router-dom";
import "./blocks/DocCard.css";
import "./blocks/shared.css";

const VALIDATION_LABELS = {
  VALIDATED: { label: "Validado ARCA", tone: "success" },
  OBSERVED: { label: "Observado", tone: "warning" },
  REJECTED: { label: "Rechazado", tone: "error" },
  EXPIRED: { label: "CAE vencido", tone: "error" },
  PENDING: { label: "Pendiente", tone: "default" },
};

function md(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*.+?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function formatMoney(n) {
  if (typeof n === "string") return n;
  return `$ ${Number(n).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
}

export function ValidationBadge({ status, confidence }) {
  const v = VALIDATION_LABELS[status] || { label: status, tone: "default" };
  return (
    <span className={`rs-val-badge rs-val-${v.tone}`}>
      {v.label}
      {confidence != null ? ` · ${Math.round(confidence * 100)}% conf.` : ""}
    </span>
  );
}

export function KpiRow({ items = [] }) {
  return (
    <div className="rs-kpi-row">
      {items.map((k, i) => (
        <div key={i} className={`rs-kpi rs-kpi-${k.tone || "default"}`}>
          <div className="rs-kpi-label">{k.label}</div>
          <div className="rs-kpi-val">{k.value}</div>
        </div>
      ))}
    </div>
  );
}

export function BarChart({ title, data = [] }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="rs-chart">
      {title && <div className="rs-chart-title">{title}</div>}
      {data.map((d, i) => (
        <div key={i} className="rs-bar-row">
          <span className="rs-bar-label">{d.label}</span>
          <div className="rs-bar-track">
            <i style={{ width: `${(d.value / max) * 100}%` }} />
          </div>
          <span className="rs-bar-val">{formatMoney(d.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function LineChart({ title, data = [] }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  const pts = data.map((d, i) => {
    const x = 10 + (i / Math.max(data.length - 1, 1)) * 300;
    const y = 110 - (d.value / max) * 90;
    return `${x},${y}`;
  });
  return (
    <div className="rs-chart">
      {title && <div className="rs-chart-title">{title}</div>}
      <svg className="rs-line" viewBox="0 0 320 120" preserveAspectRatio="none">
        <polyline fill="none" stroke="#0066cc" strokeWidth="2.5" points={pts.join(" ")} />
        <polygon fill="rgba(0,102,204,.08)" points={`${pts.join(" ")} 310,110 10,110`} />
      </svg>
      <div className="rs-line-labels">
        {data.map((d, i) => (
          <span key={i}>{d.label}</span>
        ))}
      </div>
    </div>
  );
}

export function DonutChart({ title, data = [] }) {
  const total = data.reduce((a, d) => a + d.value, 0) || 1;
  const cols = ["#0066cc", "#059669", "#7c3aed", "#d97706"];
  let start = -90;
  const arcs = data.map((d, i) => {
    const angle = (d.value / total) * 360;
    const end = start + angle;
    const large = angle > 180 ? 1 : 0;
    const r = 42;
    const cx = 50;
    const cy = 50;
    const x1 = cx + r * Math.cos((start * Math.PI) / 180);
    const y1 = cy + r * Math.sin((start * Math.PI) / 180);
    const x2 = cx + r * Math.cos((end * Math.PI) / 180);
    const y2 = cy + r * Math.sin((end * Math.PI) / 180);
    start = end;
    if (angle >= 359.9) {
      return <circle key={i} cx={cx} cy={cy} r={r} fill={cols[i % cols.length]} />;
    }
    return (
      <path
        key={i}
        d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`}
        fill={cols[i % cols.length]}
      />
    );
  });
  return (
    <div className="rs-chart rs-donut-wrap">
      {title && <div className="rs-chart-title">{title}</div>}
      <div className="rs-donut-inner">
        <svg width="100" height="100" viewBox="0 0 100 100">
          {arcs}
          <circle cx="50" cy="50" r="24" fill="#fff" />
          <text x="50" y="54" textAnchor="middle" fontSize="11" fontWeight="700" fill="#111827">
            {data.length}
          </text>
        </svg>
        <div className="rs-donut-legend">
          {data.map((d, i) => (
            <div key={i}>
              <span className="sw" style={{ background: cols[i % cols.length] }} />
              {d.label} · {formatMoney(d.value)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function columnLabel(col) {
  if (col == null) return "";
  if (typeof col === "string" || typeof col === "number") return String(col);
  if (typeof col === "object") {
    return col.label ?? col.title ?? col.name ?? col.key ?? "";
  }
  return String(col);
}

function columnKey(col, index) {
  if (col && typeof col === "object" && col.key != null) return String(col.key);
  if (typeof col === "string") return col;
  return columnLabel(col) || String(index);
}

function cellText(cell) {
  if (cell == null) return "";
  if (typeof cell === "object") {
    return cell.label ?? cell.value ?? cell.text ?? JSON.stringify(cell);
  }
  return cell;
}

export function DataTable({ columns = [], rows = [] }) {
  const labels = columns.map(columnLabel);
  const keys = columns.map(columnKey);
  return (
    <div className="rs-table-wrap">
      <table className="rs-table">
        <thead>
          <tr>
            {labels.map((label, i) => (
              <th key={i}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci}>
                  {labels[ci] === "ARCA" || keys[ci] === "ARCA" ? (
                    <ValidationBadge status={cell} />
                  ) : (
                    cellText(cell)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DocCard({ document_id, filename, supplier, amount, validation }) {
  return (
    <Link className="rs-doc-card" to={`/Filemanager?doc=${document_id || ""}`}>
      <div className="rs-doc-icon">📄</div>
      <div className="rs-doc-body">
        <div className="rs-doc-name">{filename}</div>
        <div className="rs-doc-meta">
          {supplier} · {amount}
        </div>
      </div>
      <ValidationBadge status={validation} />
      <span className="rs-doc-arrow">→</span>
    </Link>
  );
}

export function Callout({ tone = "info", children }) {
  return <div className={`rs-callout rs-callout-${tone}`}>{md(children)}</div>;
}

function normalizeTableBlock(b) {
  const data = b.data && typeof b.data === "object" && !Array.isArray(b.data) ? b.data : {};
  const rawColumns = b.columns || b.headers || data.headers || data.columns || [];
  const columnKeys = rawColumns.map(columnKey);
  const columns = rawColumns.map(columnLabel);
  let rows = b.rows ?? data.rows ?? [];

  if (!Array.isArray(rows)) {
    rows = [];
  } else {
    rows = rows.map((row) => {
      if (Array.isArray(row)) return row.map(cellText);
      if (row && typeof row === "object") {
        if (columnKeys.length && columnKeys.some((k) => Object.prototype.hasOwnProperty.call(row, k))) {
          return columnKeys.map((k) => cellText(row[k]));
        }
        if (columns.length && columns.every((c) => Object.prototype.hasOwnProperty.call(row, c))) {
          return columns.map((c) => cellText(row[c]));
        }
        return Object.values(row).map(cellText);
      }
      return [cellText(row)];
    });
  }

  return { ...b, columns, rows, data: undefined };
}

function normalizeBlock(block) {
  if (!block || typeof block !== "object") return block;
  const b = { ...block };
  if (b.type === "text") {
    b.markdown = b.markdown || b.content || "";
  } else if (b.type === "kpi_row") {
    b.items = b.items || b.kpis || [];
  } else if (b.type === "callout") {
    b.markdown = b.markdown || b.content || "";
    b.tone = b.tone || b.variant || "info";
  } else if (b.type === "table") {
    return normalizeTableBlock(b);
  }
  return b;
}

const REGISTRY = {
  text: (b) => <div className="rs-text">{md(b.markdown)}</div>,
  kpi_row: (b) => <KpiRow items={b.items} />,
  bar_chart: (b) => <BarChart title={b.title} data={b.data} />,
  line_chart: (b) => <LineChart title={b.title} data={b.data} />,
  donut_chart: (b) => <DonutChart title={b.title} data={b.data} />,
  table: (b) => <DataTable columns={b.columns} rows={b.rows} />,
  doc_card: (b) => <DocCard {...b} />,
  validation_badge: (b) => <ValidationBadge status={b.status} confidence={b.confidence} />,
  callout: (b) => <Callout tone={b.tone}>{b.markdown}</Callout>,
  divider: () => <hr className="rs-divider" />,
};

export function RenderSpec({ spec }) {
  if (!spec?.blocks?.length) return null;
  return (
    <div className="rs-wrap">
      {spec.blocks.map((raw, i) => {
        const b = normalizeBlock(raw);
        const R = REGISTRY[b.type];
        return R ? (
          <div key={i} className={`rs-block rs-block-${b.type}`}>
            {R(b)}
          </div>
        ) : null;
      })}
    </div>
  );
}
